import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	WritableSignal,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { FileFormatContentTypeMap } from '@app/shared/ui-components/au-file-loader/helpers/file-utilities';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { NewFileLoaderService } from '@app/shared/services/new-file-loader.service';
import { formatCmmsLocationsToInput } from '@app/domain/locations/type-translations';
import { IAttachedFiles, IWorkOrder } from '@app/domain/cmms-types/workorder-types';
import { ICMMSLocation } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { IDroppedFile } from '@app/shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { FileViewerModalComponent } from '@app/shared/ui-components/au-file-loader/file-viewer-modal/file-viewer-modal.component';

@Component({
	selector: 'entity-image-display',
	imports: [
		CommonModule,
		NgxTolgeeModule,
		MatIconModule,
		MatButtonModule,
		MatRippleModule,
		NgOptimizedImage,
	],
	templateUrl: './entity-image-display.component.html',
	styleUrl: './entity-image-display.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityImageDisplayComponent {
	private readonly newFileLoaderService = inject(NewFileLoaderService);
	private readonly dialog = inject(MatDialog);

	workOrder = input<IWorkOrder>();
	rawFiles = input<File[]>();
	newEntity = input<boolean>(false);
	imageChanged = output<IAttachedFiles[]>();
	removeFile = output<File>();

	fileTypesConfig: string = this.newFileLoaderService.fileTypesConfigToFileTypes();

	images = computed(() => {
		if (this.workOrder()?.files?.length) {
			return this.workOrder().files.filter(
				file =>
					file.file?.contentType === FileFormatContentTypeMap.JPEG ||
					file.file?.contentType === FileFormatContentTypeMap.PNG
			);
		}
		return [];
	});

	rawImages = computed(() => {
		return this.rawFiles()?.map(file => ({
			url: URL.createObjectURL(file),
			file: file,
		}));
	});

	allImages = computed(() => {
		const imgs = [];
		if (this.images()?.length) {
			imgs.push(...this.images());
		}
		if (this.rawImages()?.length) {
			imgs.push(...this.rawImages());
		}
		return imgs;
	});

	filesForUpload: WritableSignal<File[]> = signal<File[]>([]);
	filesList: WritableSignal<Partial<IDroppedFile>[]> =
		this.newFileLoaderService.filesList;

	constructor() {
		effect(() => {
			if (
				this.newFileLoaderService.filesFinishedLoading() ===
					this.filesForUpload().length &&
				this.filesForUpload().length > 0
			) {
				const emitUploadedFiles = this.filesList().map(file => ({
					file: {
						url: file.fileMetadata.file.url,
						contentType: file.contentType,
					},
					created: new Date(),
					...file,
				}));
				this.imageChanged.emit([
					...this.workOrder()?.files,
					...(emitUploadedFiles as IAttachedFiles[]),
				]);
				this.filesForUpload.set([]);
				this.newFileLoaderService.resetFilesList();
			}
		});
	}

	fileChanged(event: Event): void {
		const files: File[] = Array.from((event.target as HTMLInputElement).files);
		this.filesForUpload.set(files);
		this.newFileLoaderService.startLoadProcedure(
			files,
			formatCmmsLocationsToInput((this.workOrder()?.locations as ICMMSLocation[]) || [])
		);
	}

	openImage(index: number): void {
		if (this.newEntity()) {
			return;
		}
		const formattedImages = this.allImages().map(image => {
			return {
				contentType: image.file?.contentType,
				name: image.file?.name,
				url: image.file?.url,
				pk: image.file?.pk,
			};
		});

		this.dialog.open(FileViewerModalComponent, {
			data: {
				files: formattedImages,
				index,
			},
			panelClass: ['au-modal', 'no-max-width'],
		});
	}
}
